import { create } from 'zustand';
import type { MediaItem } from '../models/types';
import { StorageService } from '../services/storage';

type SortMode = 'artist' | 'title';

interface FavoritesState {
  favorites: MediaItem[];
  searchText: string;
  sortMode: SortMode;

  // Actions
  loadFavorites: () => Promise<void>;
  addToFavorites: (item: MediaItem) => Promise<void>;
  removeFromFavorites: (index: number) => Promise<void>;
  isFavorite: (id: string) => boolean;
  toggleSortMode: () => void;
  setSearchText: (text: string) => void;
  getFilteredFavorites: () => MediaItem[];
}

const sortByArtist = (a: MediaItem, b: MediaItem): number => {
  const aArtist = (a.artist || '').toLowerCase();
  const bArtist = (b.artist || '').toLowerCase();
  if (aArtist === bArtist) {
    const aTitle = a.title.toLowerCase();
    const bTitle = b.title.toLowerCase();
    if (aTitle === bTitle) {
      return (a.album || '').toLowerCase().localeCompare((b.album || '').toLowerCase());
    }
    return aTitle.localeCompare(bTitle);
  }
  return aArtist.localeCompare(bArtist);
};

const sortByTitle = (a: MediaItem, b: MediaItem): number => {
  const aTitle = a.title.toLowerCase();
  const bTitle = b.title.toLowerCase();
  if (aTitle === bTitle) {
    const aArtist = (a.artist || '').toLowerCase();
    const bArtist = (b.artist || '').toLowerCase();
    if (aArtist === bArtist) {
      return (a.album || '').toLowerCase().localeCompare((b.album || '').toLowerCase());
    }
    return aArtist.localeCompare(bArtist);
  }
  return aTitle.localeCompare(bTitle);
};

const sortFavorites = (favorites: MediaItem[], mode: SortMode): MediaItem[] => {
  return [...favorites].sort(mode === 'artist' ? sortByArtist : sortByTitle);
};

const serializeFavorites = (favorites: MediaItem[]): string => {
  let result = '';
  for (const item of favorites) {
    result += `${item.id}\n`;
    result += `${item.artist || ''}\n`;
    result += `${item.album || ''}\n`;
    result += `${item.title}\n`;
    result += `${item.genre || ''}\n`;
    result += `${item.duration || 0}\n`;
    result += `${item.extras?.collection || 'n/a'}\n`;
    result += `${item.extras?.show_id || item.extras?.showId || 'n/a'}\n`;
    result += `${item.extras?.song_id || item.extras?.songId || 'n/a'}\n`;
  }
  return result;
};

const deserializeFavorites = (data: string): MediaItem[] => {
  if (!data) return [];
  const lines = data.split('\n');

  // Remove trailing empty lines
  while (lines.length > 0 && lines[lines.length - 1] === '') {
    lines.pop();
  }

  const items: MediaItem[] = [];
  const fieldStep = 9;

  for (let i = 0; i + 8 < lines.length; i += fieldStep) {
    items.push({
      id: lines[i],
      artist: lines[i + 1],
      album: lines[i + 2],
      title: lines[i + 3],
      genre: lines[i + 4],
      duration: parseInt(lines[i + 5]) || 0,
      extras: {
        collection: lines[i + 6],
        show_id: lines[i + 7],
        song_id: lines[i + 8],
      },
    });
  }

  return items;
};

export const useFavoritesStore = create<FavoritesState>((set, get) => ({
  favorites: [],
  searchText: '',
  sortMode: 'artist',

  loadFavorites: async () => {
    const data = await StorageService.getFavorites();
    const loaded = deserializeFavorites(data);
    set({ favorites: sortFavorites(loaded, get().sortMode) });
  },

  addToFavorites: async (item: MediaItem) => {
    const { favorites, sortMode } = get();
    if (favorites.some(f => f.id === item.id)) return;

    const updated = sortFavorites([...favorites, item], sortMode);
    set({ favorites: updated });
    await StorageService.clearFavorites();
    await StorageService.setFavorites(serializeFavorites(updated));
  },

  removeFromFavorites: async (index: number) => {
    const { favorites } = get();
    const updated = [...favorites];
    updated.splice(index, 1);
    set({ favorites: updated });
    await StorageService.clearFavorites();
    await StorageService.setFavorites(serializeFavorites(updated));
  },

  isFavorite: (id: string) => {
    return get().favorites.some(f => f.id === id);
  },

  toggleSortMode: () => {
    const { favorites, sortMode } = get();
    const newMode: SortMode = sortMode === 'artist' ? 'title' : 'artist';
    set({
      sortMode: newMode,
      favorites: sortFavorites(favorites, newMode),
    });
  },

  setSearchText: (text: string) => set({ searchText: text }),

  getFilteredFavorites: () => {
    const { favorites, searchText } = get();
    if (!searchText) return favorites;
    const lower = searchText.toLowerCase();
    return favorites.filter(item =>
      item.title.toLowerCase().includes(lower) ||
      (item.artist || '').toLowerCase().includes(lower) ||
      (item.album || '').toLowerCase().includes(lower)
    );
  },
}));
